import React, {useState, useEffect, useRef, useMemo} from 'react';
import styles from './Production.module.css';
import cx from 'classnames';
import HerstellerFilter from './HerstellerFilter';
import CategoryTree from './CategoryTree';
import ArticlesList from './ArticlesList';
import MerkmaleFilter from './MerkmaleFilter';
import {SelectedMerkmal, Merkmal} from '../../../types/Merkmal';
import {OnboardingArticle} from '../../../types/Article';
import {useOnboardingContext} from '../../../contexts/OnboardingContext';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown, faChevronUp} from '@fortawesome/free-solid-svg-icons';
import {ColorService} from '../../../services/ColorService';
import {Color} from '../../../types/Color';
import {autoSelectColorMerkmale, getColorNamesFromHex} from '../../../helpers/merkmalAutoSelectHelper';
import {branchCategoryIdsKey, resolveBranchCategoryIds} from '../../../helpers/branchCategoryHelper';
import {useOnboardingCategories} from './hooks/useOnboardingCategories';

interface ProductionStepProps {
    isActive?: boolean;
}

const ProductionStep: React.FC<ProductionStepProps> = ({isActive = false}) => {
    const {formData, setSelectedArticles} = useOnboardingContext();
    const selectedHerstellerIds = useMemo(
        () => formData.selectedHerstellers.map((h) => h.id),
        [formData.selectedHerstellers]
    );
    const branchCategoryIds = useMemo(
        () => resolveBranchCategoryIds(formData.selectedTypes),
        [formData.selectedTypes]
    );
    const branchCategoriesKey = useMemo(
        () => branchCategoryIdsKey(branchCategoryIds),
        [branchCategoryIds]
    );
    const { categories, loading: categoriesLoading } = useOnboardingCategories(
        branchCategoryIds,
        isActive
    );
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [selectedMerkmale, setSelectedMerkmale] = useState<SelectedMerkmal[]>([]);
    const [availableMerkmale, setAvailableMerkmale] = useState<Merkmal[]>([]);
    const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
    const [colorObjects, setColorObjects] = useState<Color[]>([]);
    const lastProcessedColorsRef = useRef<string>('');
    const userDeselectedColorsRef = useRef<Set<string>>(new Set());
    const lastProcessedMerkmaleHashRef = useRef<string>('');
    const lastValidMerkmaleRef = useRef<Merkmal[]>([]);

    useEffect(() => {
        ColorService.getAllColors().then(setColorObjects).catch(console.error);
    }, []);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        if (branchCategoryIds.length > 0) {
            setSelectedCategoryId(branchCategoryIds[0]);
        } else {
            setSelectedCategoryId(null);
        }

        setSelectedMerkmale([]);
        setAvailableMerkmale([]);
        lastProcessedColorsRef.current = '';
        lastProcessedMerkmaleHashRef.current = '';
        lastValidMerkmaleRef.current = [];
    }, [branchCategoriesKey, isActive]);

    useEffect(() => {
        if (!isActive || availableMerkmale.length === 0 || colorObjects.length === 0) {
            return;
        }

        const merkmaleHash = JSON.stringify(availableMerkmale.map(m => m.id).sort());
        const {primaryColorName, secondaryColorName} = getColorNamesFromHex(
            formData.primaryColor,
            formData.secondaryColor,
            colorObjects
        );

        const currentColorKey = `${primaryColorName || ''}_${secondaryColorName || ''}`;
        const combinedKey = `${currentColorKey}_${merkmaleHash}`;
        const lastCombinedKey = `${lastProcessedColorsRef.current}_${lastProcessedMerkmaleHashRef.current}`;

        if (combinedKey === lastCombinedKey) {
            return;
        }

        lastProcessedColorsRef.current = currentColorKey;
        lastProcessedMerkmaleHashRef.current = merkmaleHash;

        setSelectedMerkmale(prev => {
            const result = autoSelectColorMerkmale(
                formData.primaryColor,
                formData.secondaryColor,
                colorObjects,
                availableMerkmale,
                prev,
                userDeselectedColorsRef.current
            );
            return result.shouldUpdate ? result.newSelections : prev;
        });
    }, [formData.primaryColor, formData.secondaryColor, isActive, colorObjects, availableMerkmale]);

    const handleCategorySelect = (categoryId: number) => {
        if (selectedCategoryId === categoryId) {
            const isMain =
                branchCategoryIds.includes(categoryId) ||
                categories.some((root) => root.id === categoryId);
            if (isMain) {
                return;
            }
            setSelectedCategoryId(branchCategoryIds[0] ?? categories[0]?.id ?? null);
        } else {
            setSelectedCategoryId(categoryId);
        }

        setSelectedMerkmale([]);
        setAvailableMerkmale([]);
        lastProcessedColorsRef.current = '';
        lastProcessedMerkmaleHashRef.current = '';
    };

    const handleMerkmaleLoaded = (merkmale: Merkmal[]) => {
        if (merkmale.length === 0 && selectedMerkmale.length > 0 && lastValidMerkmaleRef.current.length > 0) {
            setAvailableMerkmale(lastValidMerkmaleRef.current);
            return;
        }

        if (merkmale.length > 0) {
            lastValidMerkmaleRef.current = merkmale;
        }

        setAvailableMerkmale(merkmale);
    };

    const handleArticleSelect = (article: OnboardingArticle) => {
        const currentArticles = formData.selectedArticles;
        const isSelected = currentArticles.some(a => a.id === article.id);

        if (isSelected) {
            setSelectedArticles(currentArticles.filter(a => a.id !== article.id));
        } else {
            setSelectedArticles([...currentArticles, article]);
        }
    };

    const handleMerkmaleChange = (merkmale: SelectedMerkmal[]) => {
        const farbeMerkmal = availableMerkmale.find(m =>
            m.name.toLowerCase() === 'farbe' || m.name.toLowerCase() === 'color'
        );

        if (farbeMerkmal) {
            const currentFarbeSelections = merkmale
                .filter(m => m.kMerkmal === farbeMerkmal.id)
                .map(m => m.kMerkmalWert);

            farbeMerkmal.values.forEach(wert => {
                const wasSelected = selectedMerkmale.some(m => m.kMerkmalWert === wert.id);
                const isNowSelected = currentFarbeSelections.includes(wert.id);

                if (wasSelected && !isNowSelected) {
                    userDeselectedColorsRef.current.add(wert.name.toLowerCase());
                }
            });
        }

        setSelectedMerkmale(merkmale);
    };

    const toggleFilters = () => {
        setIsFiltersOpen(!isFiltersOpen);
    };

    return (
        <div className={cx(styles['production-step-container'])} hidden={!isActive}>
            <h2 className={styles['step-title']}>Artikelauswahl</h2>

            <HerstellerFilter
                isActive={isActive}
                branchCategoryIds={branchCategoryIds}
                categoryId={selectedCategoryId}
            />

            <div className={cx(styles['mobile-filters'])}>
                <button
                    className={cx(styles['mobile-filters-toggle'])}
                    onClick={toggleFilters}
                    type="button"
                >
                    <span>Filter</span>
                    <FontAwesomeIcon
                        icon={isFiltersOpen ? faChevronUp : faChevronDown}
                        className={cx(styles['mobile-filters-icon'])}
                    />
                </button>
                <div className={cx(
                    styles['mobile-filters-content'],
                    isFiltersOpen && styles['mobile-filters-content-open']
                )}>
                    <div className={cx(styles['mobile-filters-section'])}>
                        <CategoryTree
                            categories={categories}
                            loading={categoriesLoading}
                            onCategorySelect={handleCategorySelect}
                            selectedCategoryId={selectedCategoryId}
                        />
                    </div>
                    {availableMerkmale.length > 0 && (
                        <div className={cx(styles['mobile-filters-section'])}>
                            <MerkmaleFilter
                                merkmale={availableMerkmale}
                                isActive={isActive}
                                selectedMerkmale={selectedMerkmale}
                                onMerkmaleChange={handleMerkmaleChange}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className={cx(styles['production-content'])}>
                <div className={cx(styles['production-left'])}>
                    <CategoryTree
                        categories={categories}
                        loading={categoriesLoading}
                        onCategorySelect={handleCategorySelect}
                        selectedCategoryId={selectedCategoryId}
                    />
                </div>
                <div className={cx(styles['production-right'])}>
                    <ArticlesList
                        categoryId={selectedCategoryId}
                        branchCategoryIds={branchCategoryIds}
                        herstellerIds={selectedHerstellerIds}
                        isActive={isActive}
                        selectedArticles={formData.selectedArticles}
                        selectedMerkmale={selectedMerkmale}
                        availableMerkmale={availableMerkmale}
                        onArticleSelect={handleArticleSelect}
                        onMerkmaleLoaded={handleMerkmaleLoaded}
                        onMerkmaleChange={handleMerkmaleChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProductionStep;
